import { deviceTokenService } from '~/services/auth/DeviceToken';
/**
 * Playback Status Service
 *
 * Responds to SSE "command" events with action "playback_status" by reporting
 * the player's real-time playback state to the server:
 *   - Full flat playlist (id, name, duration, campanhaId)
 *   - Current index in the playlist
 *   - Elapsed seconds on the current item
 *   - Playlist version (to detect staleness)
 *   - Campaign grouping (unique campaigns present in the playlist)
 *
 * Unlike the backend's getPlaybackPreview (which computes what the player *should*
 * play), this reports what the player *is actually* playing right now — including
 * any on-device state like trigger interruptions, sync lag, etc.
 */
import { streamStore } from '~/stores';
import { playbackStore } from '~/stores/playback.store';
import { serverNow, getClockDiagnostics } from '~/services/time/serverClock';

/**
 * O `<video>` que está NA TELA. Com o motor agendado (A/B) há dois elementos no DOM, e o
 * primeiro pode ser o standby parado no último frame do vídeo anterior — lê-lo reportava
 * "+10s" num player em fase (medido no grupo 15, 2026-10-09). O ativo carrega
 * `data-content-id`; o standby não. Sem motor agendado há um só elemento e o fallback é ele.
 */
function activeVideoElement(): HTMLVideoElement | null {
  return (
    document.querySelector<HTMLVideoElement>('video[data-content-id]') ??
    document.querySelector<HTMLVideoElement>('video')
  );
}

/**
 * CP11 (2026-05-16) — Sentinela usado no requestId pra indicar report
 * proativo (sem comando SSE prévio). O backend usa pra rotear pro cache
 * "latest by deviceId" em vez de procurar um CommandResult pendente.
 */
export const LIVE_REPORT_REQUEST_ID = 'live';

class PlaybackStatusService {
  private apiUrl: string = '';
  private deviceId: string = '';

  configure(apiUrl: string, deviceId: string): void {
    this.apiUrl = apiUrl;
    this.deviceId = deviceId;
  }

  /**
   * Collects current playback state and uploads to server.
   */
  async report(requestId: string): Promise<void> {
    // Report acontece em alta frequência (heartbeat) — debug
    console.debug('[PlaybackStatus] Report requested, requestId:', requestId);

    if (!this.apiUrl || !this.deviceId) {
      console.error(
        '[PlaybackStatus] Not configured (missing apiUrl or deviceId)',
      );
      return;
    }

    try {
      const snapshot = this.collectSnapshot();
      await this.upload(requestId, snapshot);
    } catch (error) {
      console.error('[PlaybackStatus] Report failed:', error);
    }
  }

  /**
   * Collects the current playback snapshot from MobX stores and DOM.
   */
  private collectSnapshot(): Record<string, unknown> {
    const playlist = streamStore.playlist || [];
    const currentIndex = streamStore.currentIndex || 0;
    const currentMedia = streamStore.currentMedia;
    const playlistVersion = streamStore.playlistVersion || 0;
    const activeCampaignId = streamStore.activeCampaignId || null;
    const isTrigger = streamStore.isTriggerPlayback || false;
    const isPlaying = streamStore.isPlaying || false;

    // Elapsed on current item — use wall-clock from streamStore.currentItemStartedAt
    // as the source of truth (works for images AND videos, and respects duration
    // overrides shorter than the asset's natural length). Fall back to the <video>
    // element's currentTime only if the wall-clock marker is missing/invalid.
    const currentItemDurationSec = currentMedia?.duration ?? 0;
    let elapsedOnCurrentSec = 0;
    try {
      const startedAt = streamStore.currentItemStartedAt;
      if (startedAt && Number.isFinite(startedAt)) {
        const wallClock = Math.max(
          0,
          Math.floor((Date.now() - startedAt) / 1000),
        );
        // VÍDEO QUE ENTROU NO MEIO DO SLOT. O sync canônico dá seek pra posição do grupo (ex.:
        // entra em 15s), mas `currentItemStartedAt` marca a TROCA DE ÍNDICE — o relógio de
        // parede reportava ~0s com a mídia em 15s, e o painel acusava "15s atrasado" num player
        // correto. O maior dos dois resolve sem mexer no estado de reprodução: seek pra frente →
        // currentTime ganha; vídeo em loop manual (mais curto que o slot) ou AVPlay (o <video> do
        // DOM fica parado) → o relógio de parede ganha, como antes.
        let posicao = wallClock;
        if (currentMedia?.type === 'video') {
          const videoEl = activeVideoElement();
          const midia = videoEl?.currentTime;
          // Na borda da troca o elemento ainda segura o vídeo ANTERIOR parado no fim
          // (currentTime = duração): isso reportava "+10s" num player em fase e enganava o
          // supervisor. Posição no fim ou além do slot não é deste item → fica o relógio de parede.
          const dentroDoItem =
            midia != null &&
            Number.isFinite(midia) &&
            (currentItemDurationSec <= 0 || midia < currentItemDurationSec);
          if (dentroDoItem) {
            posicao = Math.max(posicao, Math.floor(midia as number));
          }
        }
        // Clamp to item duration — we never report more than the item's configured duration
        elapsedOnCurrentSec =
          currentItemDurationSec > 0
            ? Math.min(posicao, currentItemDurationSec)
            : posicao;
      } else {
        const videoEl = activeVideoElement();
        if (
          videoEl &&
          currentMedia?.type === 'video' &&
          !Number.isNaN(videoEl.currentTime)
        ) {
          elapsedOnCurrentSec = Math.max(0, Math.floor(videoEl.currentTime));
        }
      }
    } catch (err) {
      console.warn('[PlaybackStatus] Failed to compute elapsed', err);
    }

    // Build flat playlist
    const items = playlist.map((it, idx) => ({
      index: idx,
      id: it.id,
      name: it.name,
      type: it.type,
      duration_seconds: it.duration ?? 10,
      campanha_id: it.campanhaId ?? null,
      mime_type: it.mimeType ?? null,
      zona_id: it.zonaId ?? null,
    }));

    // ONDE estamos no loop, em segundos. É a MESMA soma que o servidor fazia a partir do
    // `items[]` que ia neste payload — durações até o índice atual mais o decorrido do item.
    //
    // Mandar o resultado em vez da matéria-prima é o que permite o `items[]` sair daqui: ele
    // existia no relatório só para que três consumidores (supervisor, dispersão e a régua do
    // admin) refizessem esta conta de fora. Eram ~71 KB de mediana medidos em produção para
    // transportar um número.
    //
    // Soma SIMPLES, sem o dedup de zona que o `total_loop_duration_seconds` aplica: é
    // exatamente o que os três faziam, e mudar o critério aqui alteraria o número exibido sem
    // ninguém ter pedido.
    let positionInLoopSeconds = 0;
    for (let i = 0; i < currentIndex && i < items.length; i++) {
      const d = items[i].duration_seconds;
      positionInLoopSeconds += d > 0 ? d : 0;
    }
    positionInLoopSeconds += Math.max(0, elapsedOnCurrentSec);

    // Total loop duration (zone groups count once, not N times)
    let totalLoopDuration = 0;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.zona_id != null && i > 0) {
        const prev = items[i - 1];
        if (prev.zona_id != null && prev.campanha_id === it.campanha_id)
          continue;
      }
      totalLoopDuration += it.duration_seconds || 0;
    }

    // CP11 — Enriquece snapshot com info de Tier 2/3 (playlist + tier).
    // Em Tier 4 (legado), playbackStore.currentSlotSequence é null e os
    // campos abaixo ficam undefined — admin renderiza como "Tier 4 legado".
    const tier = playbackStore.currentTier;
    const slotSeq = playbackStore.currentSlotSequence;
    const clock = getClockDiagnostics();

    return {
      device_id: this.deviceId,
      // Estado do relógio: deixa o painel dizer se um desvio é de RELÓGIO (offset velho,
      // sem amostra) ou de SEQUÊNCIA (player com loop diferente dos peers).
      clock_offset_ms: Math.round(clock.offsetMs),
      clock_source: clock.source,
      clock_sample_age_ms: clock.sampleAgeMs,
      clock_rtt_ms: clock.lastRttMs,
      clock_tentative: clock.tentative,
      // Relógio do SERVIDOR, não do device: é com ele que o painel compara a posição reportada
      // contra a régua canônica. Com o relógio do device, um tablet com hora errada apareceria
      // fora de fase sem estar.
      reported_at: new Date(serverNow()).toISOString(),
      is_playing: isPlaying,
      is_trigger: isTrigger,
      active_campaign_id: activeCampaignId,
      playlist_version: playlistVersion,
      current_index: currentIndex,
      current_item: currentMedia
        ? {
            id: currentMedia.id,
            name: currentMedia.name,
            type: currentMedia.type,
            duration_seconds: currentItemDurationSec,
            campanha_id: currentMedia.campanhaId ?? null,
          }
        : null,
      elapsed_on_current_seconds: elapsedOnCurrentSec,
      total_loop_duration_seconds: totalLoopDuration,
      // Substitui `items[]` + `campaigns[]`: o loop em si quem sabe é o servidor (ele montou a
      // slot_sequence), e o admin já o recebe pelo /playback-preview. Daqui vai só a posição.
      position_in_loop_seconds: positionInLoopSeconds,
      // CP11 — Tier/playlist info (undefined em Tier 4)
      tier,
      playlist_id: slotSeq?.playlistId,
      playlist_name: slotSeq?.playlistName,
      hour_window_start: slotSeq?.hourWindowStart,
      // Época que ESTE player está usando pra ancorar. O supervisor do servidor compara
      // com a dos peers do grupo: diferente = sequência velha → impulso de re-sync.
      canonical_epoch: playbackStore.canonicalEpoch,
    };
  }

  /**
   * CP11 — Envia report proativo (sem comando SSE prévio). Backend cacheia
   * indexado por deviceId; admin lê via {@code GET .../playback-status/latest}.
   * Idempotente, fire-and-forget — falhas logam mas não bloqueiam playback.
   */
  async reportLive(): Promise<void> {
    return this.report(LIVE_REPORT_REQUEST_ID);
  }

  /**
   * Upload snapshot to server.
   */
  private async upload(
    requestId: string,
    status: Record<string, unknown>,
  ): Promise<void> {
    const url = `${this.apiUrl}/api/devices/${encodeURIComponent(this.deviceId)}/playback-status`;
    // Upload high-frequency — debug
    console.debug('[PlaybackStatus] Uploading to:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': this.deviceId,
        'X-Device-Token': deviceTokenService.getToken(),
      },
      body: JSON.stringify({ requestId, status }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.error('[PlaybackStatus] Upload failed:', response.status, text);
      return;
    }
    // 🔴 CAUSA-RAIZ do renderer-kill do embedded (medido no Tab A7, 2026-09-18).
    // O caminho de SUCESSO descartava a `Response` sem ler nem cancelar o corpo. O
    // Chromium mantém então o data pipe / shared memory da resposta vivo, e no
    // renderer 32-bit isso reserva ~2 MB de address space POR REQUISIÇÃO que nunca
    // volta pro SO. A ~10 uploads/min o VmSize subia ~20 MB/min (com VmRSS PLANA em
    // ~110 MB — é reserva, não retenção) até o mmap falhar perto do teto de 32-bit e
    // o renderer ser morto (SIGTRAP) em poucas horas.
    // A/B no aparelho, mesma playlist, janelas de 8 min: sem drenar = +20,4 MB/min;
    // drenando = +0,6 MB/min.
    // `cancel()` descarta sem materializar os bytes (a resposta é só um ack).
    await response.body?.cancel().catch(() => undefined);
    console.debug('[PlaybackStatus] Upload successful');
  }
}

const playbackStatusService = new PlaybackStatusService();
export default playbackStatusService;
